<template>
  <div
    id="depositModal"
    class="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center p-4 hidden"
  >
    <div
      class="w-full max-w-md rounded-2xl p-6 shadow-2xl bg-[#0C0E1B] border border-orange-500 relative text-white"
    >
      <!-- Botão de fechar -->
      <button
        class="absolute top-3 right-3 text-white hover:text-[#d600c0] transition-colors text-xl z-10"
        aria-label="Fechar modal"
        @click="close"
      >
        &times;
      </button>

      <!-- Conteúdo -->
      <h2 class="text-white text-2xl font-semibold mb-4 text-center">
        Deposito
      </h2>

      <div class="space-y-4">
        <!-- Formulário de valor -->
        <div v-if="!pixQRCode">
          <div>
            <label class="text-gray-400 text-sm mb-1 block">Voltar</label>
            <input
              v-model.number="valor"
              type="number"
              class="w-full px-4 py-2 rounded-md bg-[#10182C] border border-[#2D3A5C] text-white"
              placeholder="Ex: 500"
            />
          </div>

          <div>
            <label class="text-gray-400 text-sm mb-1 block">Moeda</label>
            <select
              v-model="moeda"
              class="w-full px-4 py-2 rounded-md bg-[#10182C] border border-[#2D3A5C] text-white"
            >
              <option value="BRL">BRL</option>
            </select>
          </div>

          <VaButton
            class="w-full mt-4 font-semibold"
            color="#F97316"
            text-color="#fff"
            @click="confirmar"
          >
            <span v-if="!isLoading">Confirmar</span>
            <span v-else>Carregando...</span>
          </VaButton>

          <p v-if="mensagem" class="text-center text-sm text-red mt-2">
            {{ mensagem }}
          </p>
        </div>

        <!-- Exibição de PIX -->
        <div v-else class="text-center space-y-4">
          <!-- QR Code gerado -->
          <img :src="pixQRCode" alt="QR Code PIX" class="mx-auto w-48 h-48" />

          <!-- Linha digitável -->
          <div>
            <label class="text-gray-400 text-sm mb-1 block"
              >Linha digitável</label
            >
            <div class="flex items-center">
              <input
                v-model="pixCopy"
                readonly
                class="w-full px-4 py-2 rounded-md bg-[#10182C] border border-[#2D3A5C] text-white mr-2"
              />
              <button
                class="px-4 py-2 bg-orange-500 hover:bg-orange-600 rounded-md font-semibold"
                @click="copyPix"
              >
                Copiar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import QRCode from "qrcode";
import { Deposit, DepositSchema } from "@/gen/api/v1/api_pb";
import { useToast } from "vuestic-ui";
import { create } from "@bufbuild/protobuf";

import { useDepositStore } from "@/stores/deposit";
import { useAuthStore } from "@/stores/auth";

const depositStore = useDepositStore();
const authStore = useAuthStore();
const { init } = useToast();

const valor = ref<number>(0);
const isLoading = ref<boolean>(false);
const moeda = ref<string>("BRL");
const mensagem = ref<string>("");
// Para dados PIX
const pixPayload = ref<string>("");
const pixQRCode = ref<string>("");
const pixCopy = ref<string>("");
const pixId = ref<number | string>("");
const pixStatus = ref<string>("");
const pixExternalId = ref<string>("");
const pixFee = ref<number>(0);

/** Fecha modal e reseta estado */
const close = () => {
  document.getElementById("depositModal")?.classList.add("hidden");
  valor.value = 0;
  mensagem.value = "";
  pixPayload.value = "";
  pixQRCode.value = "";
  pixCopy.value = "";
  pixId.value = "";
  pixStatus.value = "";
  pixExternalId.value = "";
  pixFee.value = 0;
};

/** Copia linha digitável para área de transferência */
const copyPix = () => {
  if (pixCopy.value) {
    navigator.clipboard.writeText(pixCopy.value);
    init({ message: "Linha digitável copiada!", color: "success" });
  }
};

async function confirmar() {
  if (!valor.value || valor.value <= 0) {
    mensagem.value = "Informe um valor válido.";
    return;
  }
  const user = authStore.user;
  if (!user) {
    mensagem.value = "Usuário não autenticado.";
    return;
  }

  isLoading.value = true;
  try {
    const req = create(DepositSchema, {
      id: 1,
      userId: user.id,
      amount: valor.value,
    });
    const resp = await depositStore.createDeposit(req);

    if (!resp) throw new Error("Response vazia");

    interface FullDeposit extends Deposit {
      pixQrCode: string;
      status: string;
      externalId: string;
      fee: number;
    }

    const dp = resp as unknown as FullDeposit;

    pixPayload.value = dp.pixQrCode;
    pixCopy.value = dp.pixQrCode;
    pixId.value = dp.id;
    pixStatus.value = dp.status;
    pixExternalId.value = dp.externalId;
    pixFee.value = dp.fee;

    await QRCode.toDataURL(dp.pixQrCode)
      .then((url: string) => (pixQRCode.value = url))
      .catch((e: Error) => {
        console.error(e);
        mensagem.value = "Falha ao gerar QR code.";
      });
  } catch (e: any) {
    mensagem.value = e.message || "Erro ao processar depósito.";
  } finally {
    isLoading.value = false;
  }
}
</script>
